import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReferralService } from '@pg-app/partner/referral/services/referral.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { format } from 'date-fns';
import { RoleNames } from '@pg-app/core/services/roles/RoleNames';
import { UtilityService } from '@nttp/common-service/utility-service';
import { DialogUploadReferralComponent } from '@pg-app/partner/referral/dialog-upload-referral/dialog-upload-referral.component';
import { CommonDialogComponent } from '@pg-app/common/common-dialog/common-dialog.component';
import { ShareService } from '@nttp/common-service/share-service';
import { DownloadFileTypeEnum } from '@nttp/enum/download-file-type-enum';
import { CommonMessages } from '@pg-app/shared/constants/common-messages';
import { environment } from '@pg-env/environment';

export interface CommonModel {
  name: string;
  id: string;
}

export interface Currency {
  code: string;
  id: string;
  flag: string;
}

@Component({
  selector: 'np-referral',
  templateUrl: './referral.component.html',
  styleUrls: ['./referral.component.scss']
})
export class ReferralComponent implements OnInit {
  showIndustry = false;
  isSavingFail = false;
  providerTooltip = '';
  currencyBoughtTooltip = '';
  currencySoldTooltip = '';
  fg: FormGroup;
  uploadForm: FormGroup;
  targetFile: File;
  titles: CommonModel[] = [];
  currencies: Currency[];
  frequencies: CommonModel[] = [];
  leadTypes: CommonModel[] = [];
  providerLists: CommonModel[] = [];
  reasonLists: CommonModel[] = [];
  clientLists: CommonModel[] = [];
  invalidFile = false;
  isBulkUpload = false;
  phonePattern = '^[0-9*#+]+$';
  // user name can contains characters (a-z, A-Z, 0-9, dash -,  apostrophe ', space), no special characters
  namePattern = `([A-Za-z0-9\-' ]+)`;
  industrys = [];
  destroy$ = new Subject<boolean>();
  showAddDetail = false;

  SEND_REFERRAL = RoleNames.SEND_REFERRAL;
  commonMessage = CommonMessages;
  brandName = environment.brandName;

  get fileNameCtrl(): FormControl {
    return this.uploadForm.get('fileName') as FormControl;
  }
  get fileCtrl(): FormControl {
    return this.uploadForm.get('file') as FormControl;
  }
  get busCtrl(): FormControl {
    return this.fg.get('business') as FormControl;
  }

  get titleCtrl(): FormControl {
    return this.fg.get('title') as FormControl;
  }

  get firstNameCtrl(): FormControl {
    return this.fg.get('firstName') as FormControl;
  }

  get middleNameCtrl(): FormControl {
    return this.fg.get('middleName') as FormControl;
  }

  get lastNameCtrl(): FormControl {
    return this.fg.get('lastName') as FormControl;
  }

  get landlineCtrl(): FormControl {
    return this.fg.get('landline') as FormControl;
  }

  get phoneCtrl(): FormControl {
    return this.fg.get('phone') as FormControl;
  }

  get emailCtrl(): FormControl {
    return this.fg.get('email') as FormControl;
  }

  get companyNumberCtrl(): FormControl {
    return this.fg.get('companyNumber') as FormControl;
  }

  get leadTypeCtrl(): FormControl {
    return this.fg.get('leadType') as FormControl;
  }

  get tradeDateCtrl(): FormControl {
    return this.fg.get('tradeDate') as FormControl;
  }

  get amountCtrl(): FormControl {
    return this.fg.get('amount') as FormControl;
  }

  get currencyBoughtCtrl(): FormControl {
    return this.fg.get('currencyBought') as FormControl;
  }

  get currencySoldCtrl(): FormControl {
    return this.fg.get('currencySold') as FormControl;
  }

  get industryCtrl(): FormControl {
    return this.fg.get('industry') as FormControl;
  }
  get frequencyCtrl(): FormControl {
    return this.fg.get('frequencyNeed') as FormControl;
  }
  get reasonCtrl(): FormControl {
    return this.fg.get('reasonPurchase') as FormControl;
  }

  get currentProviderCtrl(): FormControl {
    return this.fg.get('currentProvider') as FormControl;
  }
  get volumeCtrl(): FormControl {
    return this.fg.get('volume') as FormControl;
  }
  get clientNeedCtrl(): FormControl {
    return this.fg.get('clientNeed') as FormControl;
  }
  get noteCtrl(): FormControl {
    return this.fg.get('note') as FormControl;
  }

  constructor(
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private referralService: ReferralService,
    public toastr: ToastrService,
    private utilityService: UtilityService,
    private shareService: ShareService,
  ) {}

  ngOnInit() {
    this.isBulkUpload = this.data.isBulkUpload || false;
    this.uploadForm = this.fb.group({
      fileName: '',
      file: ''
    });
    this.fg = this.fb.group({
      business: '',
      title: ['', Validators.required],
      firstName: ['', [Validators.required  , Validators.pattern(this.namePattern), Validators.maxLength(255)]],
      middleName: ['', [Validators.pattern(this.namePattern), Validators.maxLength(255)]],
      lastName: ['', [Validators.required  , Validators.pattern(this.namePattern), Validators.maxLength(255)]],
      landline: ['', [Validators.required, Validators.pattern(this.phonePattern), Validators.maxLength(20)]],
      phone: ['', [Validators.pattern(this.phonePattern), Validators.maxLength(20)]],
      email: ['', [Validators.required, Validators.email]],
      leadType: ['', Validators.required],
      amount: ['', Validators.required],
      tradeDate: [''],
      currencyBought: ['', Validators.required],
      currencySold: ['', Validators.required],
      industry: '',
      reasonPurchase: null,
      currentProvider: '',
      volume: null,
      clientNeed: '',
      note: '',
      frequencyNeed: '',
      companyNumber: ''
    });
    this.leadTypeCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const busCtrl = this.fg.get('business');
      if (value && value === 1) {
        this.showIndustry = true;
        busCtrl.setValidators([Validators.required]);
      } else {
        this.showIndustry = false;
        busCtrl.setValidators([]);
      }
      busCtrl.updateValueAndValidity();
    });

    this.currentProviderCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const length = value.length;
      this.providerTooltip = '';
      for (let i = 0; i < length; i++) {
        this.providerTooltip = this.providerTooltip + ', ' + value[i];
      }
      this.providerTooltip = this.providerTooltip.substring(1, this.providerTooltip.length);
    });

    this.currencyBoughtCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const length = value.length;
      this.currencyBoughtTooltip = '';
      for (let i = 0; i < length; i++) {
        this.currencyBoughtTooltip = this.currencyBoughtTooltip + ', ' + value[i];
      }
      this.currencyBoughtTooltip = this.currencyBoughtTooltip.substring(1, this.currencyBoughtTooltip.length);
    });
    this.currencySoldCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      const length = value.length;
      this.currencySoldTooltip = '';
      for (let i = 0; i < length; i++) {
        this.currencySoldTooltip = this.currencySoldTooltip + ', ' + value[i];
      }
      this.currencySoldTooltip = this.currencySoldTooltip.substring(1, this.currencySoldTooltip.length);
    });

    this.getCurrencies();
    this.getTitles();
    this.getLeadTypes();
    this.getFrequencyLists();
    this.getProviderLists();
    this.getReasonLists();
    this.getClientLists();
    this.getIndustry();

    this.fg.get('email').setValue(this.data.email);
    this.fg.get('lastName').setValue(this.data.lastName);
    this.fg.get('firstName').setValue(this.data.firstName);
  }

  onSubmit() {
    this.showAddDetail = true;
    this.fg.markAllAsTouched();
    if (!this.fg.valid) {
      return;
    }
    const param = {
      email: this.emailCtrl.value
    };
    this.referralService.checkEmailExist(param).subscribe(data => {
      if (!data.Exists) {
        this.addReferral();
      } else {
        this.toastr.error(`This email address is already registered with ${this.brandName}`);
      }
    });
  }

  uploadFile() {
    this.uploadReferral();
  }
  notUploadFile() {
    if (this.fileNameCtrl.value === '') {
      return true;
    } else {
      return false;
    }
  }
  onChange(event: Event) {
    const validExts = new Array('.xlsx', '.xls', '.csv');
    const files = (<HTMLInputElement>event.target).files;
    let file: File = null;
    if (files.length > 0) {
      file = files[0];
      this.targetFile = file;
      const fileExt = file.name.substring(file.name.lastIndexOf('.'));
      if (validExts.indexOf(fileExt) < 0) {
        this.fileNameCtrl.setValue('');
        this.fileCtrl.setValue(null);
        this.invalidFile = true;
      } else {
        this.fileNameCtrl.setValue(file.name);
        this.invalidFile = false;
      }
    }
  }

  deleteFile() {
    this.fileCtrl.setValue(null);
    this.fileNameCtrl.setValue('');
  }
  private addReferral() {
    const referral = {
      title: this.titleCtrl.value,
      firstName: this.firstNameCtrl.value,
      middleName: this.middleNameCtrl.value ? this.middleNameCtrl.value : null,
      lastName: this.lastNameCtrl.value,
      email: this.emailCtrl.value,
      phoneNumber: this.phoneCtrl.value,
      businessName: this.busCtrl.value ? this.busCtrl.value : null,
      currenciesBought: this.currencyBoughtTooltip ? this.currencyBoughtTooltip : null,
      currenciesSold: this.currencySoldTooltip ? this.currencySoldTooltip : null,
      landline: this.landlineCtrl.value ? this.landlineCtrl.value : null,
      leadType: this.leadTypeCtrl.value.toString(),
      nextTradeDate: this.tradeDateCtrl.value ? format(this.tradeDateCtrl.value, 'YYYYMMDD') : null,
      amountRequired: this.amountCtrl.value ? parseFloat(this.amountCtrl.value) : null,
      industry: this.showIndustry ? this.industryCtrl.value : null,
      frequencyOfNeed: this.frequencyCtrl.value ? this.frequencyCtrl.value : null,
      reasonForPurchase: this.reasonCtrl.value ? this.reasonCtrl.value : null,
      annualVolume: this.amountCtrl.value ? parseFloat(this.volumeCtrl.value) : null,
      clientneeds: this.clientNeedCtrl.value ? this.clientNeedCtrl.value : null,
      additionalNote: this.noteCtrl.value ? this.noteCtrl.value : null,
      currentProviders: this.providerTooltip ? this.providerTooltip : null,
      companyNumber: this.companyNumberCtrl.value ? this.companyNumberCtrl.value : null
    };

    this.referralService.addReferral(referral).subscribe(
      data => {
        const dialogRef = this.dialog.open(CommonDialogComponent, {
          data: {
            title: 'Upload completed successfully',
            dialogType: 1
          }
        });
        dialogRef.afterClosed().subscribe(result => {
          window.location.reload();
        });
        this.isSavingFail = false;
      }
    );
  }

  private uploadReferral() {
    const fileUpload = this.targetFile;
    this.referralService.uploadReferral(fileUpload).subscribe(
      data => {
        const dialogRef = this.dialog.open(DialogUploadReferralComponent, {
          data: data,
          disableClose: true,
          panelClass: 'custom-dialog'
        });
        dialogRef.afterClosed().subscribe(result => {
          this.fileNameCtrl.setValue('');
          this.fileCtrl.setValue(null);
        });
      },
      err => {
        const message = err?.error?.messages?.length > 0 ? err.error.messages[0]
          : 'Invalid File<br>Please use the correct template file.';
        const dialogRef = this.dialog.open(CommonDialogComponent, {
          data: {
            title: message,
            dialogType: 1
          }
        });
        dialogRef.afterClosed().subscribe(result => {
          this.fileNameCtrl.setValue('');
          this.fileCtrl.setValue(null);
        });
      }
    );
  }

  togglePicker(picker) {
    picker.opened ? picker.close() : picker.open();
  }

  private getTitles() {
    this.shareService.titleList$.subscribe((data: CommonModel[]) => {
      this.titles = data || [];
      const defaultTitle = this.titles.find(item => item.name === 'Mr');
      this.fg.get('title').setValue(defaultTitle.name);
    });
  }

  private getLeadTypes() {
    this.referralService.getLeadTypes().subscribe((data: CommonModel[]) => {
      this.leadTypes = data || [];
    });
  }

  private getCurrencies() {
    this.utilityService.getCurrencies().subscribe(data => {
      this.currencies = data || [];
    });
  }

  private getFrequencyLists() {
    this.shareService.getFrequencies().subscribe((data: CommonModel[]) => {
      this.frequencies = data || [];
    });
  }
  private getReasonLists() {
    this.shareService.getReasonForPurchases().subscribe((data: CommonModel[]) => {
      this.reasonLists = data || [];
    });
  }
  private getProviderLists() {
    this.referralService.getProviderLists().subscribe((data: CommonModel[]) => {
      this.providerLists = data || [];
    });
  }
  private getClientLists() {
    this.referralService.getClientLists().subscribe((data: CommonModel[]) => {
      this.clientLists = data || [];
    });
  }

  onDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  getIndustry() {
    this.referralService.getIndustry().subscribe((data: CommonModel[]) => {
      this.industrys = data || [];
    });
  }

  downloadTemplate() {
    this.referralService.downloadReferralTemplate().subscribe(res => {
      const fileName = `Referral_Template.xlsx`;
      this.utilityService.downloadFile(
        res,
        fileName,
        DownloadFileTypeEnum.Excel
      );
    });
  }
}
